import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { activitiesApi, activityKeys } from '@/features/activities';
import { useNewTasks, useNewTaskFilters, useNewTaskMutations, useNewTaskStatusCount } from '../hooks';
import { useNewTaskColumns, TasksDataTable, TaskStatusTabs, TaskDialog, DeleteTaskDialog } from '../components';
import type { Task, TaskStatus } from '../types';

interface ViewConfig {
  titleKey: string;
  statuses?: TaskStatus[];
  /** @deprecated use responsibleAttorneyId / partnerId instead */
  scopeToCurrentUser?: boolean;
  hideAssignedTo?: boolean;
  showStatusTabs?: boolean;
  rootOnly?: boolean;
  defaultAssignedToCurrentUser?: boolean;
  /** Pass `responsibleAttorneyId=currentUserId` to scope lead-review board */
  useResponsibleAttorneyScope?: boolean;
  /** Pass `partnerId=currentUserId` to scope partner-review board */
  usePartnerScope?: boolean;
  /**
   * Combined partner-review scope: pass `partnerReviewUserId=currentUserId` — the
   * server returns PARTNER_PENDING/BILLING_REJECT of matters where the user is a
   * partner PLUS LEAD_PENDING/PARTNER_REJECT of matters where the user is the lead
   * (a partner-type user may hold a matter's Lead slot, 2026-07-14).
   */
  usePartnerReviewScope?: boolean;
  /** When true, do NOT pass viewerUserId — adjustment rows stay hidden on this board */
  hideAdjustments?: boolean;
  /**
   * "My Tasks" mode — backend returns parents the user owns OR has an adjustment on,
   * with the viewer's adjustment inlined per row. Suppresses standalone adjustment rows.
   */
  useMyTasksMode?: boolean;
  /**
   * Exclude tasks assigned to the current user from the results — used by Lead /
   * Partner Review boards so the reviewer doesn't see their own self-assigned
   * tasks (those already appear on My Tasks).
   */
  excludeSelfAssigned?: boolean;
}

function TasksViewPage({ view }: { view?: ViewConfig }) {
  const { t } = useTranslation();
  const { hasAccess, user } = useAuth();
  // Seeing other people's tasks is a PERMISSION, not a role type (`task:get:all`
  // — granted to LEAD/PARTNER/MD/MANAGER roles and to the STAFF-type Office
  // Manager). Holders get the whole board plus the "Assigned To" filter;
  // everyone else is scoped to their own tasks. The server enforces the same
  // rule, so this gate is only about what the UI asks for.
  const canViewAllTasks = hasAccess('task', 'get:all');
  const { filters, updateFilter, updateFilters, resetFilters, setStatus } = useNewTaskFilters(
    { preserveAssignedStaffId: !canViewAllTasks }
  );

  const pageTitle = view ? t(view.titleKey) : t('navigation.newTasks');

  // On the main tasks page: if the URL has assignedStaffId equal to their own ID,
  // clear it on mount for anyone who may see everyone. The old NavMain always
  // auto-set this; holders of `task:get:all` should see all tasks by default.
  const ownIdClearedOnMount = React.useRef(false);
  React.useEffect(() => {
    if (!ownIdClearedOnMount.current && canViewAllTasks && !view && user?.id && filters.assignedStaffId === user.id) {
      ownIdClearedOnMount.current = true;
      updateFilter('assignedStaffId', undefined);
    }
  }, [canViewAllTasks, view, user?.id, filters.assignedStaffId, updateFilter]);

  // Set default assignedStaffId for pages that need it
  const defaultApplied = React.useRef(false);
  React.useEffect(() => {
    if (view?.defaultAssignedToCurrentUser && user?.id && !defaultApplied.current && !filters.assignedStaffId) {
      defaultApplied.current = true;
      updateFilter('assignedStaffId', user.id);
    }
  }, [view?.defaultAssignedToCurrentUser, user?.id, filters.assignedStaffId, updateFilter]);

  // A `task:get:all` holder sees exactly what the "Assigned To" filter says —
  // `undefined` = everyone, matching the "All" the dropdown shows. (Previously
  // LEAD/PARTNER fell back to their own id when the filter was cleared, so "All"
  // was ignored and the board stayed stuck on their own tasks.) Everyone else is
  // scoped to their own tasks — the server enforces this too.
  const resolvedAssignedStaffId = canViewAllTasks
    ? filters.assignedStaffId
    : user?.id;

  const effectiveFilters = view
    ? {
        ...filters,
        assignedStaffId: resolvedAssignedStaffId,
        ...(view.statuses ? { status: undefined, statuses: view.statuses } : {}),
        // Legacy: scopeToCurrentUser used assignedStaffId — no longer needed
        // for review boards; kept only for backward compatibility with non-review
        // views that may still use it.
        ...(view.scopeToCurrentUser && user?.id && !view.useResponsibleAttorneyScope && !view.usePartnerScope && !view.usePartnerReviewScope && !view.useMyTasksMode
          ? { assignedStaffId: user.id }
          : {}),
        ...(view.rootOnly ? { parentId: null as null } : {}),
        // Review-board scoping: server filters by matter membership
        // Review boards scope by matter membership, not by assignee — so drop the
        // self-fallback, but keep an explicitly-picked assignee from the filter so
        // a lead/partner can still narrow the board to one person.
        ...(view.useResponsibleAttorneyScope && user?.id
          ? { responsibleAttorneyId: user.id, assignedStaffId: filters.assignedStaffId }
          : {}),
        ...(view.usePartnerScope && user?.id
          ? { partnerId: user.id, assignedStaffId: filters.assignedStaffId }
          : {}),
        ...(view.usePartnerReviewScope && user?.id
          ? { partnerReviewUserId: user.id, assignedStaffId: filters.assignedStaffId }
          : {}),
        // My Tasks mode: backend broadens the scope and inlines viewer adjustments.
        // Drop assignedStaffId/viewerUserId — myTasksUserId takes over both.
        ...(view.useMyTasksMode && user?.id
          ? { myTasksUserId: user.id, assignedStaffId: undefined }
          : {}),
        // Review boards: hide tasks the viewer self-assigned (those show on My Tasks).
        ...(view.excludeSelfAssigned && user?.id
          ? { excludeAssignedStaffId: user.id }
          : {}),
        // Review boards (hideAdjustments) hide adjustment rows entirely; other
        // boards (e.g. My Tasks) show them as their own rows. viewerUserId still
        // inlines the viewer's own adjustment on its parent row where applicable.
        excludeAdjustments: view.hideAdjustments ? true : false,
        ...(user?.id && !view.hideAdjustments && !view.useMyTasksMode
          ? { viewerUserId: user.id }
          : {}),
      }
    : {
        ...filters,
        assignedStaffId: resolvedAssignedStaffId,
        // On the main tasks board: do NOT show adjustment tasks as their own rows.
        // viewerUserId is intentionally NOT sent — with it the backend returns the
        // viewer's own adjustments as standalone rows; without it the server falls
        // back to the authenticated caller for the inline viewerAdjustment, so
        // Start/Stop/Finish on the parent row still works.
        excludeAdjustments: true,
      };

  const { data, isLoading, isError, error, refetch } = useNewTasks(effectiveFilters);
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();
  const { data: statusCounts } = useNewTaskStatusCount(effectiveFilters);
  const { deleteMutation } = useNewTaskMutations();

  const [taskToDelete, setTaskToDelete] = React.useState<Task | null>(null);
  const [taskToEdit, setTaskToEdit] = React.useState<Task |   null>(null);
  const [expandedTasks, setExpandedTasks] = React.useState<Record<number, boolean>>({});
  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);
  const [loadingTasks, setLoadingTasks] = React.useState<Record<number, boolean>>({});

  const { data: activeActivity } = useQuery({
    queryKey: activityKeys.active(),
    queryFn: async () => {
      try {
        return await activitiesApi.getActive();
      } catch {
        return null;
      }
    },
    retry: 1,
    refetchOnMount: false,
  });

  const handleDelete = async () => {
    if (taskToDelete) {
      await deleteMutation.mutateAsync(taskToDelete.id);
      setTaskToDelete(null);
    }
  };

  const handleActivityAction = () => void refetch();
  const handleActionSuccess = () => void refetch();

  const handleToggleExpand = (taskId: number) => {
    setExpandedTasks((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleSort = (field: string) => {
    if (filters.orderBy !== field) {
      updateFilters({ orderBy: field, order: 'asc' });
    } else if (filters.order === 'asc') {
      updateFilters({ order: 'desc' });
    } else if (filters.order === 'desc') {
      updateFilters({ orderBy: undefined, order: undefined });
    } else {
      updateFilters({ order: 'asc' });
    }
  };

  const userInfo = user
    ? { id: user.id, role: user.role ? { name: user.role.name, type: user.role.type } : undefined }
    : null;

  const columns = useNewTaskColumns({
    onEdit: setTaskToEdit,
    onDelete: setTaskToDelete,
    onStartActivity: handleActivityAction,
    onStopActivity: handleActivityAction,
    onActionSuccess: handleActionSuccess,
    onToggleExpand: handleToggleExpand,
    expandedTasks,
    loadingTasks,
    hasAccess,
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    activeActivity: activeActivity
      ? { id: activeActivity.id, taskId: activeActivity.taskId ?? 0 }
      : null,
    userInfo,
  });

  // An exact-id lookup (notification deep-link `?id=`) shows the task whatever
  // its stage — the status tabs are hidden and the data query skips newStatus.
  const showStatusTabs = filters.id
    ? false
    : view
      ? (view.showStatusTabs ?? false)
      : true;

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{pageTitle}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('task-type', 'get') && (
            <Link to="/app/settings/task-types">
              <Button variant="outline">{t('taskTypes')}</Button>
            </Link>
          )}
          {hasAccess('task', 'create') && (
            <TaskDialog onSuccess={() => void refetch()} />
          )}
        </div>
      </div>

      {showStatusTabs && (
        <div className="px-4 pt-4">
          <TaskStatusTabs value={filters.status} onChange={setStatus} statusCounts={statusCounts} />
        </div>
      )}

      <div className="px-4 py-4">
        <TasksDataTable
          tableZoom={tableZoom}
          controlsContainer={tableControlsEl}
          data={data?.results ?? []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          updateFilters={updateFilters}
          onClearFilters={resetFilters}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          expandedTasks={expandedTasks}
          defaultFiltersExpanded
          hideAssignedTo={view?.hideAssignedTo}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>

      <TaskDialog
        taskId={taskToEdit?.id}
        open={!!taskToEdit}
        onOpenChange={(open) => !open && setTaskToEdit(null)}
        onSuccess={() => { setTaskToEdit(null); void refetch(); }}
      />

      <DeleteTaskDialog
        open={!!taskToDelete}
        onOpenChange={(open) => !open && setTaskToDelete(null)}
        task={taskToDelete}
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}

export function NewTasksPage() {
  return <TasksViewPage />;
}

export function MyTasksPage() {
  // Tasks the current user is the assignee of AND can actively work on (start/stop).
  // The actionable status set is CUMULATIVE by role, because a user holds different
  // membership roles on different matters: a global LEAD can be staff on matter X,
  // so they still need to see their NEW/LEAD_REJECT staff work there — not only the
  // lead-stage tasks. So each role inherits every lower role's statuses:
  //   STAFF   → NEW, LEAD_REJECT
  //   LEAD    → + LEAD_PENDING, PARTNER_REJECT
  //   PARTNER → + LEAD_PENDING, PARTNER_REJECT (2026-07-14: a partner-type user
  //             may be a matter's LEAD, where their own work tasks are born at
  //             LEAD_PENDING and partner-tier rejects land as PARTNER_REJECT),
  //             plus PARTNER_PENDING, BILLING_REJECT (partner-assignee tasks are
  //             born at PARTNER_PENDING; MD rejects land as BILLING_REJECT).
  //             Partners are still never matter-staff, so NEW/LEAD_REJECT can
  //             never be theirs to work.
  //   MD      → BILLING_PENDING only (user decision 2026-06-07): an MD-assigned
  //             task is only ever born at BILLING_PENDING (MD self-assign — no
  //             one else may assign/reassign a task to an MD).
  // DONE/BILLED stay hidden so the board remains "actionable". Adjustment rows are
  // hidden entirely on this board — adjustment work happens on the review boards
  // where `viewerAdjustment` is inlined on the parent row.
  const { user } = useAuth();
  const roleType = user?.role?.type;
  const staffStatuses: TaskStatus[] = ['NEW', 'LEAD_REJECT'];
  const leadStatuses: TaskStatus[] = [...staffStatuses, 'LEAD_PENDING', 'PARTNER_REJECT'];
  const partnerStatuses: TaskStatus[] = ['LEAD_PENDING', 'PARTNER_REJECT', 'PARTNER_PENDING', 'BILLING_REJECT'];
  const mdStatuses: TaskStatus[] = ['BILLING_PENDING'];
  let myStatuses: TaskStatus[] | undefined;
  if (roleType === 'STAFF') myStatuses = staffStatuses;
  else if (roleType === 'LEAD') myStatuses = leadStatuses;
  else if (roleType === 'PARTNER') myStatuses = partnerStatuses;
  else if (roleType === 'MD') myStatuses = mdStatuses;

  return (
    <TasksViewPage
      view={{
        titleKey: 'navigation.myTasks',
        ...(myStatuses ? { statuses: myStatuses } : {}),
        scopeToCurrentUser: true,
        hideAssignedTo: true,
        // Show the user's OWN adjustment tasks as rows too (scoped by
        // assignedStaffId=user.id + the actionable status curation above).
        showStatusTabs: false,
      }}
    />
  );
}

export function LeadReviewPage() {
  // Scope to matters where current user is the responsible attorney (lead).
  // The server filters tasks by responsibleAttorneyId, not assignedStaffId.
  // Exclude tasks the viewer self-assigned — those already appear on My Tasks.
  return (
    <TasksViewPage
      view={{
        titleKey: 'navigation.leadReview',
        statuses: ['LEAD_PENDING', 'PARTNER_REJECT'],
        useResponsibleAttorneyScope: true,
        hideAssignedTo: false,
        hideAdjustments: true,
        excludeSelfAssigned: true,
      }}
    />
  );
}

export function PartnerReviewPage() {
  // MD users also use this page; for MD there is no matter-partner filter
  // needed — the server will return all partner-pending tasks for MD.
  // For PARTNER the board is the COMBINED review queue (2026-07-14): the server
  // pairs each status set with its own matter-membership scope —
  // PARTNER_PENDING/BILLING_REJECT where the user is a partner, plus
  // LEAD_PENDING/PARTNER_REJECT where the user is the LEAD (partner-as-lead;
  // the Lead Review page stays LEAD-type only). The statuses list here is the
  // union — it drives the tabs/counts; the server clause does the pairing.
  const { user } = useAuth();
  const isMd = user?.role?.type === 'MD';

  return (
    <TasksViewPage
      view={{
        titleKey: 'navigation.partnerReview',
        ...(isMd
          ? { statuses: ['BILLING_REJECT', 'PARTNER_PENDING'] as TaskStatus[] }
          : {
              statuses: ['LEAD_PENDING', 'PARTNER_REJECT', 'PARTNER_PENDING', 'BILLING_REJECT'] as TaskStatus[],
              usePartnerReviewScope: true,
            }),
        hideAssignedTo: false,
        hideAdjustments: true,
        // Hide tasks the viewer self-assigned — those already appear on My Tasks.
        excludeSelfAssigned: true,
      }}
    />
  );
}

export function ApproveToBillingPage() {
  // MD sees all BILLING_PENDING tasks — no membership scoping needed.
  return (
    <TasksViewPage
      view={{
        titleKey: 'navigation.approveToBilling',
        statuses: ['BILLING_PENDING'],
        hideAdjustments: true,
      }}
    />
  );
}
